import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { format } from "date-fns";
import { tr } from "date-fns/locale";
import { 
  Scissors, 
  Calendar, 
  Clock, 
  User, 
  LogOut, 
  Plus,
  X,
  AlertCircle,
  Star
} from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";

const BRAND_NAME = "Salon Bahadır Kersü";

export default function CustomerDashboard() {
  const navigate = useNavigate();
  const { user, token, logout } = useAuth();
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [appointmentToCancel, setAppointmentToCancel] = useState(null);
  const [canReview, setCanReview] = useState(false);
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [reviewRating, setReviewRating] = useState(5);
  const [reviewComment, setReviewComment] = useState("");
  const [submittingReview, setSubmittingReview] = useState(false);

  useEffect(() => {
    fetchAppointments();
    checkCanReview();
  }, [token]);

  const fetchAppointments = async () => {
    try {
      const response = await axios.get(`${API}/appointments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAppointments(response.data);
    } catch (error) {
      toast.error("Randevular yüklenemedi");
    } finally {
      setLoading(false);
    }
  };

  const checkCanReview = async () => {
    try {
      const response = await axios.get(`${API}/reviews/can-review`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCanReview(response.data.can_review);
    } catch (error) {
      console.error("Could not check review status");
    }
  };

  const handleSubmitReview = async () => {
    setSubmittingReview(true);
    try {
      await axios.post(
        `${API}/reviews`,
        { rating: reviewRating, comment: reviewComment },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Yorumunuz için teşekkürler!");
      setReviewDialogOpen(false);
      setCanReview(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Yorum gönderilemedi");
    } finally {
      setSubmittingReview(false);
    }
  };

  const handleCancelAppointment = async () => {
    if (!appointmentToCancel) return;
    
    try {
      await axios.put(
        `${API}/appointments/${appointmentToCancel.id}`,
        { status: "cancelled" },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Randevu iptal edildi");
      fetchAppointments();
    } catch (error) {
      toast.error("Randevu iptal edilemedi");
    } finally {
      setCancelDialogOpen(false);
      setAppointmentToCancel(null);
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      pending: { class: "status-pending", text: "Onay Bekliyor" },
      approved: { class: "status-approved", text: "Onaylandı" },
      rejected: { class: "status-rejected", text: "Reddedildi" },
      completed: { class: "status-completed", text: "Tamamlandı" },
      cancelled: { class: "status-cancelled", text: "İptal Edildi" }
    };
    const statusInfo = statusMap[status] || { class: "", text: status };
    return <span className={`status-badge ${statusInfo.class}`}>{statusInfo.text}</span>;
  };

  const upcomingAppointments = appointments.filter(
    a => ["pending", "approved"].includes(a.status)
  );
  const pastAppointments = appointments.filter(
    a => ["completed", "rejected", "cancelled"].includes(a.status)
  );

  return (
    <div className="min-h-screen bg-[#fafafa]">
      {/* Header */}
      <header className="bg-white border-b border-[#e5e7eb] px-4 md:px-6 py-3 md:py-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <Scissors className="w-5 h-5 md:w-6 md:h-6 text-[#c9a96e]" />
            <span className="font-display text-base md:text-xl font-semibold text-[#1a1a2e]">{BRAND_NAME}</span>
          </Link>

          <div className="flex items-center gap-2 md:gap-4">
            <div className="text-right hidden sm:block">
              <p className="text-[#1a1a2e] font-medium text-sm">{user?.name || "Müşteri"}</p>
              <p className="text-[#6b7280] text-xs">{user?.phone}</p>
            </div>
            <button
              onClick={handleLogout}
              className="p-2 text-[#6b7280] hover:text-[#1a1a2e] transition-colors"
              data-testid="logout-btn"
            >
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 md:px-6 py-5 md:py-8" data-testid="customer-dashboard">
        {/* Welcome Section */}
        <div className="mb-5 md:mb-8">
          <h1 className="font-display text-xl md:text-3xl text-[#1a1a2e] mb-1">
            Hoş Geldiniz, {user?.name?.split(" ")[0] || "Değerli Müşterimiz"}
          </h1>
          <p className="text-[#6b7280] text-sm md:text-base">Randevularınızı buradan yönetebilirsiniz.</p>
        </div>

        {/* Quick Actions */}
        <div className="mb-5 md:mb-8 flex flex-col sm:flex-row gap-3 md:gap-4">
          <Link to="/booking" className="flex-1 sm:flex-none">
            <Button className="btn-primary w-full sm:w-auto" data-testid="new-appointment-btn">
              <Plus className="w-4 h-4 mr-2" />
              Yeni Randevu Al
            </Button>
          </Link>

          {canReview && (
            <Button
              onClick={() => setReviewDialogOpen(true)}
              className="btn-secondary w-full sm:w-auto"
              data-testid="write-review-btn"
            >
              <Star className="w-4 h-4 mr-2" />
              Yorum Yaz
            </Button>
          )}
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="spinner"></div>
          </div>
        ) : (
          <>
            {/* Upcoming Appointments */}
            <section className="mb-12">
              <h2 className="font-display text-lg md:text-xl text-[#1a1a2e] mb-3 md:mb-4">Yaklaşan Randevular</h2>
              
              {upcomingAppointments.length === 0 ? (
                <div className="bg-white border border-[#e5e7eb] rounded-xl p-8 text-center shadow-sm">
                  <Calendar className="w-12 h-12 text-[#9ca3af] mx-auto mb-4" />
                  <p className="text-[#6b7280] mb-4">Yaklaşan randevunuz bulunmuyor</p>
                  <Link to="/booking">
                    <Button className="btn-secondary">Randevu Al</Button>
                  </Link>
                </div>
              ) : (
                <div className="space-y-4">
                  {upcomingAppointments.map((appointment) => (
                    <div 
                      key={appointment.id}
                      className="bg-white border border-[#e5e7eb] rounded-xl p-4 md:p-6 shadow-sm"
                      data-testid={`appointment-${appointment.id}`}
                    >
                      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="font-display text-lg text-[#1a1a2e]">{appointment.service_name}</h3>
                            {getStatusBadge(appointment.status)}
                          </div>
                          
                          <div className="flex flex-wrap gap-4 text-[#6b7280] text-sm">
                            <div className="flex items-center gap-2">
                              <Calendar className="w-4 h-4" />
                              {format(new Date(appointment.date), "d MMMM yyyy", { locale: tr })}
                            </div>
                            <div className="flex items-center gap-2">
                              <Clock className="w-4 h-4" />
                              {appointment.time}
                            </div>
                            <div className="flex items-center gap-2">
                              <User className="w-4 h-4" />
                              {appointment.barber_name}
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-4">
                          <span className="font-accent text-[#c9a96e] text-lg">{appointment.service_price}₺</span>
                          
                          {appointment.status === "pending" && (
                            <Button
                              variant="ghost"
                              className="text-red-600 hover:text-red-600 hover:bg-red-600/10"
                              onClick={() => {
                                setAppointmentToCancel(appointment);
                                setCancelDialogOpen(true);
                              }}
                              data-testid={`cancel-btn-${appointment.id}`}
                            >
                              <X className="w-4 h-4 mr-1" />
                              İptal
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>

            {/* Past Appointments */}
            {pastAppointments.length > 0 && (
              <section>
                <h2 className="font-display text-xl text-[#1a1a2e] mb-4">Geçmiş Randevular</h2>
                
                <div className="space-y-4">
                  {pastAppointments.map((appointment) => (
                    <div 
                      key={appointment.id}
                      className="bg-white border border-[#e5e7eb] rounded-xl p-6 opacity-75 shadow-sm"
                      data-testid={`past-appointment-${appointment.id}`}
                    >
                      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="font-display text-lg text-[#1a1a2e]">{appointment.service_name}</h3>
                            {getStatusBadge(appointment.status)}
                          </div>
                          
                          <div className="flex flex-wrap gap-4 text-[#6b7280] text-sm">
                            <div className="flex items-center gap-2">
                              <Calendar className="w-4 h-4" />
                              {format(new Date(appointment.date), "d MMMM yyyy", { locale: tr })}
                            </div>
                            <div className="flex items-center gap-2">
                              <Clock className="w-4 h-4" />
                              {appointment.time}
                            </div>
                            <div className="flex items-center gap-2">
                              <User className="w-4 h-4" />
                              {appointment.barber_name}
                            </div>
                          </div>
                        </div>
                        
                        <span className="font-accent text-[#9ca3af] text-lg">{appointment.service_price}₺</span>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}
          </>
        )}
      </main>

      {/* Cancel Dialog */}
      <AlertDialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
        <AlertDialogContent className="bg-white border-[#e5e7eb] rounded-xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[#1a1a2e] flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-red-600" />
              Randevuyu İptal Et
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[#6b7280]">
              Bu randevuyu iptal etmek istediğinizden emin misiniz? Bu işlem geri alınamaz.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-white border-[#e5e7eb] text-[#1a1a2e] hover:bg-[#fafafa]">
              Vazgeç
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleCancelAppointment}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              İptal Et
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Review Dialog */}
      <Dialog open={reviewDialogOpen} onOpenChange={setReviewDialogOpen}>
        <DialogContent className="bg-white border-[#e5e7eb] rounded-xl text-[#1a1a2e]">
          <DialogHeader>
            <DialogTitle className="font-display">Yorum Yaz</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <label className="font-accent text-xs text-[#6b7280] mb-2 block">PUAN</label>
              <div className="flex gap-2">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    onClick={() => setReviewRating(star)}
                    className="p-1"
                  >
                    <Star 
                      className={`w-8 h-8 ${star <= reviewRating ? "text-[#c9a96e] fill-[#c9a96e]" : "text-[#9ca3af]"}`}
                    />
                  </button>
                ))}
              </div>
            </div>
            
            <div>
              <label className="font-accent text-xs text-[#6b7280] mb-2 block">YORUMUNUZ</label>
              <textarea
                value={reviewComment}
                onChange={(e) => setReviewComment(e.target.value)}
                placeholder="Deneyiminizi paylaşın..."
                className="w-full h-24 input-elite resize-none"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button 
              variant="ghost" 
              onClick={() => setReviewDialogOpen(false)}
              className="text-[#6b7280]"
            >
              İptal
            </Button>
            <Button 
              onClick={handleSubmitReview}
              className="btn-primary"
              disabled={submittingReview}
            >
              {submittingReview ? "Gönderiliyor..." : "Gönder"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
